import { Link } from "react-router-dom";
import { CalendarClock, MapPin, BedDouble } from "lucide-react";
import { CTAButton } from "@/components/CTAButton";
import { formatEventDate } from "@/lib/dates";

function shiftDay(iso, days) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString();
}

// Quick-plan block shown above the long venue guide: best stay area, suggested
// one-night-before / one-night-after stay dates, venue access, and a tracked hotel CTA
// carrying the current event + stay-area context (so the live pilot journey stays intact).
export function EventQuickPlan({ event, venue, stayArea, pageName, ctx = {} }) {
  if (!event || !venue) return null;
  const tz = event.timezone;
  const dayOpts = { weekday: "short", month: "short", day: "numeric" };
  const inLabel = formatEventDate(shiftDay(event.date, -1), tz, dayOpts);
  const outLabel = formatEventDate(shiftDay(event.date, 1), tz, dayOpts);
  const access = stayArea?._access || stayArea?.access || venue.transit_overview;

  return (
    <div
      data-testid="event-quick-plan"
      className="rounded-2xl bg-ink text-white card-shadow p-6 sm:p-8 mb-12 grid gap-8 md:grid-cols-[1.35fr_1fr]"
    >
      <div>
        <div className="overline text-primary mb-2">Your gameday plan</div>
        <h2 className="font-heading text-2xl sm:text-3xl font-black tracking-tighter leading-tight">
          Where to stay for {event.title}
        </h2>
        {stayArea && (
          <p className="text-white/70 mt-3 leading-relaxed">
            Best area:{" "}
            <span className="font-semibold text-white" data-testid="quick-plan-area">{stayArea.name}</span>
            {(stayArea._badge || stayArea.best_for) && (
              <span className="text-white/50"> — {stayArea._badge || stayArea.best_for}</span>
            )}
          </p>
        )}
        <div className="mt-6">
          {stayArea && stayArea._hotel_link_scope === "none" ? (
            <span data-testid="quick-plan-hotels-unavailable"
              className="inline-flex items-center rounded-full bg-white/10 px-5 py-2.5 text-sm font-semibold text-white/50 cursor-not-allowed">
              Hotel search unavailable
            </span>
          ) : (
            <CTAButton
              productType="hotels"
              label={stayArea ? (stayArea._hotel_link_scope === "neighborhood" ? `Find hotels in ${stayArea.name}` : "Search hotels · city-wide") : "Find hotels"}
              size="md"
              testId="quick-plan-hotels-cta"
              ctx={{ ...ctx, page: pageName, venue: venue.slug, stayArea: stayArea?.slug, city: stayArea?.city_slug || venue.city_slug, eventDate: event.date, position: "quick_plan" }}
            />
          )}
        </div>
        <p className="mt-3 text-xs text-white/50">
          Some links are affiliate links — Hotelstrips may earn a commission when you book.{" "}
          <Link to="/affiliate-disclosure" className="underline hover:text-primary">Disclosure</Link>.
        </p>
      </div>

      <div className="space-y-4 md:border-l md:border-white/10 md:pl-6">
        <PlanRow icon={CalendarClock} title="Suggested stay" testId="quick-plan-dates">
          {inLabel && outLabel ? `${inLabel} → ${outLabel}` : "One night before & after the event"}
          <span className="block text-xs text-white/50 mt-0.5">Night before + night after gameday</span>
        </PlanRow>
        {stayArea && (
          <PlanRow icon={BedDouble} title="Recommended area">
            {stayArea.name}
          </PlanRow>
        )}
        {access && (
          <PlanRow icon={MapPin} title="Venue access" testId="quick-plan-access">
            {access}
          </PlanRow>
        )}
      </div>
    </div>
  );
}

function PlanRow({ icon: Icon, title, children, testId }) {
  return (
    <div className="flex gap-3" data-testid={testId}>
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-white/10 text-primary">
        <Icon className="h-4 w-4" />
      </span>
      <div>
        <div className="overline text-white/50">{title}</div>
        <div className="text-sm text-white/90 leading-relaxed mt-0.5">{children}</div>
      </div>
    </div>
  );
}
